import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DollarSign, Save, X } from "lucide-react";
import { ProductVariant, TProductWithRelation } from '@/types/product-type';

interface PriceEditProps {
    productVariant: ProductVariant;
    onSave: (newPrice: number) => Promise<void>;
    trigger?: React.ReactNode;
    open: boolean;                 // Add this
    onOpenChange: (v: boolean) => void; // Add this
}

export function PriceEditDialog({ productVariant, onSave, trigger, open, onOpenChange }: PriceEditProps) {
    const [price, setPrice] = useState<number>(productVariant.price ?? 0);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Auto-focus input when dialog opens
    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.select(), 50);
        }
    }, [open]);

    const handleSave = async () => {
        setLoading(true);
        await onSave(price);
        setLoading(false);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                {trigger || (
                    <Button variant="ghost" className="h-8 px-2 text-green-600 font-bold">
                        ${productVariant.price?.toFixed(2)}
                    </Button>
                )}
            </DialogTrigger>

            <DialogContent className="sm:max-w-[320px] rounded-xl">
                <DialogHeader>
                    <DialogTitle className="text-slate-700 text-lg">Edit Price for : [{productVariant?.productName}]</DialogTitle>
                    <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">SALE CODE : </span>
                        <code className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-[11px] font-semibold text-slate-700 tracking-wider">
                            {productVariant?.sku}
                        </code>
                    </div>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    <div className="relative">
                        <Label htmlFor="price" className="sr-only">Price</Label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                                <DollarSign className="h-4 w-4" />
                            </span>
                            <Input
                                ref={inputRef}
                                id="price"
                                type="number"
                                step="0.01"
                                value={price}
                                onChange={(e) => setPrice(parseFloat(e.target.value))}
                                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                                className="pl-9 text-2xl font-semibold h-14 border-slate-200 focus:ring-green-500"
                            />
                        </div>
                    </div>

                    {/* Quick Adjustment Suggestions (Optional for SaaS) */}
                    <div className="flex gap-2">
                        {[-1, 1, 5].map((val) => (
                            <Button
                                key={val}
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex-1 text-[10px]"
                                onClick={() => setPrice((prev) => (prev + val))}
                            >
                                {val > 0 ? `+${val}` : val}
                            </Button>
                        ))}
                    </div>
                </div>

                <DialogFooter className="flex-row gap-2 sm:justify-end">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { onOpenChange(false); }}
                        className="flex-1 sm:flex-none text-slate-400"
                    >
                    </Button>
                    <Button
                        onClick={handleSave}
                        size="sm"
                        disabled={loading}
                        className="flex-1 sm:flex-none"
                    >
                        {loading ? "..." : "Update Price"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}